import { useMergeState } from '@/shared/lib/customHooks';
import type { DeviceManualsProps, DeviceManualsState } from './deviceManuals.types';

/**
 * Holds the sub-tab selection for the device manuals section of the support page. State starts on tab '1', and the hook exposes a tab switcher that writes only real changes plus a resolver mapping the active tab onto the instructions-for-use, patient-manual or patient-quick-guide entry of the materials prop.
 *
 * @param materials - Device manual documents to choose from.
 * @returns Sub-tab state with the tab switcher and manual url resolver.
 */
export const useDeviceManuals = ({ materials }: Pick<DeviceManualsProps, 'materials'>) => {
  const [state, setState] = useMergeState<DeviceManualsState>({
    activeTab: '1',
  });

  /**
   * Switches the device-manuals section to another manual sub-tab. The key is merged into state only when it differs from the currently active tab.
   *
   * @param tab - Key of the manual sub-tab to activate.
   * @returns Nothing.
   */
  const navigateTab = (tab: any) => {
    if (tab !== state.activeTab) {
      setState({ activeTab: tab });
    }
  };

  /**
   * Resolves the manual document belonging to the selected device-manuals sub-tab. Tabs '1', '2' and '3' map onto the materials' instructionsForUse, patientManual and patientQuickGuide entries, and any other tab yields undefined.
   *
   * @returns Manual document for the active sub-tab, or undefined.
   */
  const generateUrl = () => {
    switch (state.activeTab) {
      case '1': {
        return materials?.instructionsForUse;
      }
      case '2': {
        return materials?.patientManual;
      }
      case '3': {
        return materials?.patientQuickGuide;
      }
      default: {
        return undefined;
      }
    }
  };

  return { state, navigateTab, generateUrl };
};
